import { evidencePowerUserUpgradeModalOpenAtom } from '@/atoms/paywallAtoms';
// eslint-disable-next-line @heidi/rules/no-unguarded-apple-billing -- useSubscriptionUpgrade already guards execution internally via isAppleCompliantBuild(); this hook renders no UI
import { useSubscriptionUpgrade } from '@/features/settings/billing/hooks/useSubscriptionUpgrade';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import {
  getNewProductTierProduct,
  getPlanFromPeriod,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import useReverseTrial from '@/hooks/useReverseTrial';
import { useUserActivityActionCount } from '@/hooks/useUserActivityActionCount';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { PaywallSource } from '@/models/analytics/features/payments';
import { type Period } from '@heidi/commerce/contracts';
import { SubscriptionTier, UserActivityAction } from '@heidi/schema/ml-scribe';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

export interface UseEvidencePowerUserUpgradeModalProps {
  /** When provided, modal is controlled by a launch flow (e.g. triggered from a banner nudge). */
  launchOpen?: boolean;
  onLaunchClose?: () => void;
  /**
   * Optional override for the usage count (e.g. when previewing on /dev/trial-modals).
   * When unset, the hook reads `user_activity.evidence_chat_query` from the user query.
   */
  countOverride?: number;
}

/**
 * Business logic for the Evidence power-user upgrade modal.
 *
 * Target plan: Clinician Pro — reusing `useSubscriptionUpgrade`, same as `ClinicianUpgradeModal`.
 *
 * Count source: `user_activity.evidence_chat_query` on the user payload. After each
 * Evidence-mode send, `trackChatSubmitted` invalidates the user query so the count refetches.
 */
export function useEvidencePowerUserUpgradeModal({
  launchOpen,
  onLaunchClose,
  countOverride,
}: UseEvidencePowerUserUpgradeModalProps = {}) {
  const [isAtomOpen, setIsAtomOpen] = useAtom(evidencePowerUserUpgradeModalOpenAtom);
  const { clinicianTrialDaysLeft } = useReverseTrial();
  const { hasTeam, isTeamStripeManaged, isNonAdminTeamMember, isLoadingTeamDetails } =
    useCombinedQueriesTeamGeneralInfo();

  const isOpen = isAtomOpen || (launchOpen ?? false);

  useEffect(() => {
    if (isOpen) {
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.PaywallViewed,
        props: {
          source: PaywallSource.EvidencePowerUserUpgradeModal,
          target_plan: SubscriptionTier.CLINICIAN_PRO,
          days_left_on_trial: clinicianTrialDaysLeft,
        },
      });
    }
    // Only fire when modal open state changes, not when other props update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsAtomOpen(false);
    onLaunchClose?.();
  }, [setIsAtomOpen, onLaunchClose]);

  const { data: products, catalogueVersion } = useQueryProducts({
    heidi2Catalogue: true,
  });
  const clinicianProduct = getNewProductTierProduct(
    SubscriptionTier.CLINICIAN_PRO,
    products
  );

  const { handleUpgrade, isPending } = useSubscriptionUpgrade({
    onCheckoutComplete: handleClose,
  });

  const handleUpgradeClick = useCallback(
    (period: Period) =>
      void handleUpgrade(
        period,
        SubscriptionTier.CLINICIAN_PRO,
        catalogueVersion === 'v3'
          ? (getPlanFromPeriod(period, clinicianProduct)?.id ?? null)
          : undefined
      ),
    [catalogueVersion, clinicianProduct, handleUpgrade]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      } else {
        setIsAtomOpen(true);
      }
    },
    [handleClose, setIsAtomOpen]
  );

  const { data: serverEvidenceCount = 0, isPending: isEvidenceCountPending } =
    useUserActivityActionCount(UserActivityAction.evidence_chat_query);
  const evidenceCount = countOverride ?? serverEvidenceCount;
  const isEvidenceCountLoading = countOverride === undefined && isEvidenceCountPending;

  const shouldDisableButtonForNonStripeManagedTeam = hasTeam && !isTeamStripeManaged;
  const isUpgradeDisabledForTeamMember = !isLoadingTeamDetails && isNonAdminTeamMember;
  const isUpgradeDisabled =
    isUpgradeDisabledForTeamMember || shouldDisableButtonForNonStripeManagedTeam;

  return {
    isOpen,
    handleOpenChange,
    handleUpgradeClick,
    isPending,
    clinicianProduct,
    evidenceCount,
    isEvidenceCountLoading,
    isUpgradeDisabled,
    isUpgradeDisabledForTeamMember,
    shouldDisableButtonForNonStripeManagedTeam,
  };
}
